import { Injectable, signal } from '@angular/core';
import * as CryptoJS from 'crypto-js';
import { Boolean, LocalStorageConfig } from './local-storage.config';

@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {
  key = LocalStorageConfig.secretKey;
  private useSessionStorage = signal<boolean>(false);
  constructor() {
    const isUseSessionStorage = sessionStorage.getItem(LocalStorageConfig.useSessionStorage);
    this.updateUseSessionStorage(isUseSessionStorage == Boolean.true, false);
  }

  public updateUseSessionStorage(status: boolean, saveToSessionStorage: boolean, delay = 0) {
    this.useSessionStorage.set(status);
    if (saveToSessionStorage) {
      setTimeout(() => {
        sessionStorage.setItem(LocalStorageConfig.useSessionStorage, status ? Boolean.true : Boolean.false);
      }, delay);
    }
  }

  public saveData(key: string, value: string, forceLocalStorage = false): void {
    const storage = forceLocalStorage || !this.useSessionStorage() ? localStorage : sessionStorage;
    storage.setItem(key, this.encrypt(value));
  }

  public saveTokenWithDuration(key: string, value: { token: string; expiry: number }): void {
    const hoursInMilliseconds = value.expiry * 3600000;
    const currentTime = new Date().getTime();
    const expiryDate = currentTime + hoursInMilliseconds;

    const data: { token: string; expiry: number } = {
      token: value.token,
      expiry: expiryDate
    };

    this.saveData(key, JSON.stringify(data));
  }

  public getTokenWithDuration(key: string): string {
    const decryptedData = this.getData(key);
    if (!decryptedData) return '';

    const data: { token: string; expiry: number } = JSON.parse(decryptedData);

    const currentTime = new Date().getTime();

    if (currentTime > data.expiry) {
      this.removeData(data.token);
      return '';
    }

    return data.token;
  }

  public getData(key: string, forceLocalStorage = false) {
    const useLocal = forceLocalStorage || !this.useSessionStorage();
    const data = (useLocal ? localStorage.getItem(key) : sessionStorage.getItem(key)) || '';
    return this.decrypt(data);
  }

  public getDataObject(key: string): object | null | string {
    const dataStr = this.getData(key);
    return dataStr ? JSON.parse(dataStr) : null;
  }

  public removeData(key: string, forceLocalStorage = false) {
    if (forceLocalStorage || !this.useSessionStorage()) {
      localStorage.removeItem(key);
    } else {
      sessionStorage.removeItem(key);
    }
  }

  public clearData() {
    if (this.useSessionStorage()) {
      sessionStorage.clear();
    } else {
      localStorage.clear();
    }
  }

  private encrypt(txt: string): string {
    return CryptoJS.AES.encrypt(txt, this.key).toString();
  }

  private decrypt(txtToDecrypt: string) {
    return CryptoJS.AES.decrypt(txtToDecrypt, this.key).toString(CryptoJS.enc.Utf8);
  }
}
